import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Account } from 'shared/types/account';
import app from '@frontastic/catwalk/src/js/app/app';
import { sdk } from '../../../sdk';
import TagManager from '../classes/TagManager';
import { useNoticesContext } from '../provider/notices/NoticesContext';

const getSkusInWishlist = (lineItems) => {
  return (lineItems ?? []).map((item) => item.variant.sku);
};

const checkInWishlist = (wishlistLineItems, variants) => {
  return (
    wishlistLineItems && wishlistLineItems.find((item) => variants.find((variant) => variant.sku === item.variant.sku))
  );
};

const nextWishlistActionDefaultState = {
  action: '',
  method: () => {},
  item: {},
  lineItemId: '',
};

export const useWishlist = () => {
  const initialWishlist = useSelector((state) => state.wishlist.wishlist);
  // We basically just save passed method and any item info
  // just to pass it to our login method and trigger saved method
  // in after login callback
  const [nextWishlistAction, setNextWishlistAction] = useState(nextWishlistActionDefaultState);
  const [isLoadingWishlist, setLoadingWishlist] = useState(null);
  const [wishlist, setWishList] = useState(null);
  const [skusInWishlist, setSkusInWishlist] = useState([]);
  const [triggerPlacement, setTriggerPlacement] = useState(null);
  const { setNotices } = useNoticesContext();

  const getWishlist = async () => {
    setLoadingWishlist(true);

    try {
      const { data, isError } = await sdk.callAction({
        actionName: 'wishlist/getWishlist',
      });

      if (isError) {
        throw new Error('Failed to fetch wishlist');
      }

      setWishList(data);
      setSkusInWishlist(getSkusInWishlist(data.lineItems));
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoadingWishlist(false);
    }
  };

  const addToWishlist = async (product, variant, count, successCallback, errorCallback, placement = null) => {
    setTriggerPlacement(placement);
    setLoadingWishlist(true);

    try {
      const { data, isError } = await sdk.callAction({
        actionName: 'wishlist/addToWishlist',
        payload: { variant: { sku: variant.sku }, count },
      });

      if (isError || !data) {
        throw new Error('Failed to add product to wishlist');
      }

      setWishList(data);
      setSkusInWishlist(getSkusInWishlist(data.lineItems));

      const productName = product?.productName || product?.name;
      setNotices({ type: 'success', code: 'account.message.wishlistAdd', message: productName });
      TagManager.addToWishlist({ name: productName, ...variant, count });

      successCallback?.(data);
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      setNotices({ type: 'error', message: error.message });
      errorCallback?.();
    } finally {
      setLoadingWishlist(false);
      setTriggerPlacement(null);
    }
  };

  const removeFromWishlist = async (lineItemId, callback, errorCallback) => {
    try {
      const { data, isError } = await sdk.callAction({
        actionName: 'wishlist/removeLineItem',
        payload: { lineItem: { id: lineItemId } },
      });

      if (isError) {
        throw new Error('Failed to fetch wishlist');
      }

      setWishList(data);
      setSkusInWishlist(getSkusInWishlist(data.lineItems));
      setNotices({ type: 'success', code: 'account.message.wishlistRemove' });
      callback?.();
    } catch (error) {
      errorCallback?.();
      setNotices({ type: 'error', message: error?.message });
    }
  };

  const resetNextWishlistAction = () => setNextWishlistAction(nextWishlistActionDefaultState);

  useEffect(() => {
    if (initialWishlist?.isComplete()) {
      setSkusInWishlist(getSkusInWishlist(initialWishlist.data.lineItems));
      setWishList(initialWishlist.data);
    }
  }, [initialWishlist]);

  return {
    wishlist,
    skusInWishlist,
    isLoadingWishlist: isLoadingWishlist ?? initialWishlist?.loading,
    setLoadingWishlist,
    addToWishlist,
    removeFromWishlist,
    checkInWishlist,
    getWishlist,
    setNextWishlistAction,
    resetNextWishlistAction,
    nextWishlistAction,
    triggerPlacement,
  };
};
